import { useEffect, useCallback, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Pagination } from '../ui/pagination';
import {
  Archive,
  Download,
  Loader2,
  AlertCircle,
  ChevronDown,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { drilldownAPI } from '../../lib/api';
import { OtifReviewStatusBadge } from '../purchasing/otifDecision';
import { useView } from '../../contexts/ViewContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import type { DrilldownHeadline } from '../../contexts/DrilldownContext';
import {
  OTIF_TABS,
  OTIF_LINKABLE_FIELDS,
  formatDrilldownValue,
  createFieldLink,
  getOTIFStatusStyle,
  OTIF_BADGE_SHAPE,
  createLineStatusBadge,
  getOnTimeStatusStyle,
  downloadExportedData,
  getRowDrillUrl,
  rowDrillProps,
  ROW_DRILL_CLASS,
  type DrilldownField,
} from './drilldownUtils';
import { exportTruncationMessage, readExportTruncation, type ExportTruncation } from '../../lib/exportTruncation';
import ExportTruncationBanner from '../export/ExportTruncationBanner';
import { pageCountFromResponse, type PageSizeMemory } from '../../hooks/usePagination';
import LineGRNsModal from './LineGRNsModal';

interface OTIFDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters?: Record<string, unknown>;
  contextLabel?: string;
  /** CR-7261: the chart figure that was clicked, shown beside the context label. */
  headline?: DrilldownHeadline;
  initialTab?: 'failed' | 'arrears' | 'positive' | 'total';
}

export default function OTIFDrilldownModal({
  isOpen,
  onClose,
  filters = {},
  contextLabel,
  headline,
  initialTab,
}: OTIFDrilldownModalProps) {
  const { viewLevel, getViewQueryParams } = useView();
  const { formatDate } = useFormatters();

  const [activeTab, setActiveTab] = useState<string>(initialTab || 'failed');
  // CR-7276 rework: the second click-through. A row with a PO line opens its
  // receipts here rather than navigating away — the user is working through a
  // month's failures and asking "why did this one fail", which the GRNs answer.
  // Rows without a PO line (an NCR, a header-only row) keep the link behaviour.
  const [grnLineId, setGrnLineId] = useState<number | null>(null);
  const [grnLineLabel, setGrnLineLabel] = useState<string | undefined>(undefined);

  const openLineGRNs = useCallback((record: Record<string, unknown>) => {
    const raw = record['po_line_id'];
    if (raw === null || raw === undefined || raw === '') return;
    // CR-8242: rows are keyed by their detail field — po__po_number on the
    // PO-line metrics, po_line__po__po_number on the GRN ones.
    const po = record['po__po_number'] ?? record['po_line__po__po_number'] ?? record['po_number'];
    const line = record['line_number'] ?? record['po_line__line_number'];
    setGrnLineLabel(po ? `PO ${po}${line ? ` / Line ${line}` : ''}` : undefined);
    setGrnLineId(Number(raw));
  }, []);

  /** Row props for the GRN drill, or null when the row has no PO line. */
  const grnDrillProps = useCallback((record: Record<string, unknown>) => {
    const raw = record['po_line_id'];
    if (raw === null || raw === undefined || raw === '') return null;
    return {
      role: 'button' as const,
      tabIndex: 0,
      title: 'Show the goods receipts behind this line',
      onClick: () => openLineGRNs(record),
      onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLineGRNs(record); }
      },
    };
  }, [openLineGRNs]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<Array<Record<string, unknown>>>([]);
  const [fields, setFields] = useState<DrilldownField[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  // CR-7432: counts from the last export, when it was cut short by the row cap.
  const [truncation, setTruncation] = useState<ExportTruncation | null>(null);

  // CR-5766: Request counter to prevent stale API responses from overwriting newer data
  const fetchRequestIdRef = useRef(0);

  const fetchTabCounts = useCallback(async () => {
    const counts: Record<string, number> = {};
    const viewParams = getViewQueryParams();

    for (const tab of OTIF_TABS) {
      try {
        const response = await drilldownAPI.getDetails('otif_lines', {
          ...filters,
          ...tab.filter,
          view_level: viewLevel,
          ...viewParams,
          page: 1,
          page_size: 1,
        });
        counts[tab.key] = response.data.count;
      } catch {
        counts[tab.key] = 0;
      }
    }

    setTabCounts(counts);
  }, [filters, viewLevel, getViewQueryParams]);

  const fetchData = useCallback(async () => {
    // CR-5766: Increment request ID to track this specific request
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);
    setError(null);

    const activeTabConfig = OTIF_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.getDetails('otif_lines', {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
        page: currentPage,
        page_size: pageSize,
      });

      // CR-5766: Only update state if this is still the latest request
      // This prevents stale responses from overwriting newer data
      if (requestId !== fetchRequestIdRef.current) {
        return; // A newer request was made, ignore this stale response
      }

      const { results, count } = response.data;
      setRecords(results.records);
      setFields(results.fields);
      setTotalCount(count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(count, pageSize, results.records.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);
    } catch (err: unknown) {
      // CR-5766: Only show error if this is still the latest request
      if (requestId !== fetchRequestIdRef.current) {
        return;
      }
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load OTIF data');
    } finally {
      // CR-5766: Only clear loading if this is still the latest request
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [filters, activeTab, currentPage, pageSize, viewLevel, getViewQueryParams]);

  // CR-5766: Fix race condition - set activeTab FIRST, then fetch data
  // This effect handles modal open/close and initialTab changes
  useEffect(() => {
    if (isOpen) {
      // Set tab first before any fetching
      setActiveTab(initialTab || 'failed');
      setCurrentPage(1);
    } else {
      // Reset state when modal closes
      setActiveTab(initialTab || 'failed');
      setCurrentPage(1);
      setRecords([]);
    }
  }, [isOpen, initialTab]);

  // CR-7432: once the tab moves, the banner's counts describe a file nobody
  // is looking at any more.
  useEffect(() => { setTruncation(null); }, [activeTab, isOpen]);

  // CR-5766: Fetch data only after activeTab is set - use separate effect
  // This ensures we always fetch with the correct activeTab value
  useEffect(() => {
    if (isOpen) {
      fetchTabCounts();
      fetchData();
    }
  }, [isOpen, activeTab, fetchTabCounts, fetchData]);

  const renderCellValue = (fieldKey: string, value: unknown, record: Record<string, unknown>) => {
    const formattedValue = formatDrilldownValue(value, formatDate, record);
    const linkConfig = OTIF_LINKABLE_FIELDS[fieldKey];

    if (linkConfig && value) {
      const url = linkConfig.urlPattern(String(value), record);
      return createFieldLink(url, formattedValue);
    }

    // OTIF status badge. Read-only: the GRN drill-through was removed because
    // it only ever told the GRN side of the story, which is wrong whenever the
    // call-off warehouse basis is the one being measured on. The basis-specific
    // evidence now lives in the Measured On / Stock Ref columns instead.
    // CR-4662: Column is bound to is_otif (boolean) instead of otif_status
    // (CharField) because otif_status can be stale for imported rows.
    if (fieldKey === 'is_otif' && record['id']) {
      // CR-7270 rework: the LINE decides whether there is a verdict to print,
      // not the flag alone — an open line reads Pending however its stale
      // is_otif column reads (Aaron, 18 Sep).
      // CR-7416 (Aaron, 19 Sep): all three verdicts are badges now — Pending
      // read as a broken cell beside PASS and FAILED. The hierarchy moved into
      // the colour; `getOTIFStatusStyle` owns which one.
      // CR-7760: a call-off line judged at warehouse booking carries its
      // verdict while still open, so the flag alone is read for it.
      const { className, label } = getOTIFStatusStyle(
        value, record['warehouse_judged'] ? undefined : record['status'],
      );
      return (
        <span className={`${OTIF_BADGE_SHAPE} ${className}`}>
          {label}
        </span>
      );
    }

    // CR-7416: the Status column is the line's own badge, not its raw
    // lifecycle code. The lifecycle stays on the record and is what the verdict
    // above is read from.
    if (fieldKey === 'status') {
      return createLineStatusBadge(record);
    }

    // Style on_time_status in GRN details table
    if (fieldKey === 'on_time_status') {
      const { className, label } = getOnTimeStatusStyle(value);
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${className}`}>
          {label}
        </span>
      );
    }

    // CR-7418: the OTIF Review STATUS — Overridden / Acknowledged / Not Decided
    // — in place of CR-6190's yes/no override flag. Same badge component as the
    // PO Lines grid and the line modal, so one colour means one thing wherever
    // a decision is shown. (`otif_review` on the line table,
    // `po_line__otif_review` on the GRN tables.)
    if (fieldKey === 'otif_review' || fieldKey === 'po_line__otif_review') {
      return <OtifReviewStatusBadge value={value} />;
    }

    return formattedValue;
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExporting(true);
    setShowExportMenu(false);

    const activeTabConfig = OTIF_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.exportData('otif_lines', format, {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
      });

      downloadExportedData(response.data, `otif_lines_${activeTab}_export`, format);

      // CR-7432: warn only, so a complete export is as quiet as before.
      const truncated = readExportTruncation(response);
      setTruncation(truncated);
      if (truncated) toast.warning(exportTruncationMessage(truncated));
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error(`Export failed. The ${format.toUpperCase()} file could not be generated — please try again.`);
    } finally {
      setExporting(false);
    }
  };

  // CR-7270: this modal's tabs mix populations — Total carries the frozen arrears
  // stock beside live closed-and-judged lines, and Passed/Failed exclude the stock
  // altogether. So the label keys on whether THESE rows carry a capture date, never
  // on the tab or the mode: on a tab with no frozen rows it must not appear at all.
  const frozenAsAt = records
    .map((record) => (record as { frozen_as_at?: string | null }).frozen_as_at)
    .find((value): value is string => Boolean(value));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                OTIF Analysis
                {contextLabel && (
                  <span className="text-muted-foreground font-normal ml-2">
                    - {contextLabel}
                  </span>
                )}
                {headline && (
                  <span className="text-muted-foreground font-normal ml-2" data-testid="drilldown-headline">
                    · {headline.label} {headline.value}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                Drill down into OTIF performance by line status
              </DialogDescription>
              {frozenAsAt && (
                <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                  <Archive className="h-3.5 w-3.5" />
                  Arrears rows are cached month-end data as at {formatDate(frozenAsAt)}
                </span>
              )}
            </div>
            {(
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting || records.length === 0}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border z-50">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('xlsx')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export as Excel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <ExportTruncationBanner
            truncation={truncation}
            onDismiss={() => setTruncation(null)}
          />
        </DialogHeader>

        {/* Tab Headers with Counts */}
        {(
          <div className="border-b">
            <div className="flex">
              {OTIF_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-primary text-primary bg-primary/5'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span className="text-lg font-bold">
                      {tabCounts[tab.key]?.toLocaleString() ?? '-'}
                    </span>
                    <span className="text-xs mt-0.5">{tab.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content Area - min-h prevents layout shift during loading that causes visual jerking */}
        <div className="flex-1 min-h-[400px] overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading data...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>{error}</p>
              <Button variant="outline" size="sm" onClick={fetchData} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <p>No records found for this filter</p>
            </div>
          ) : (
            // PO Lines Table
            <div>
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    {fields.map((field) => (
                      <th
                        key={field.field}
                        className="text-left py-3 px-4 text-sm font-medium whitespace-nowrap"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr
                      key={String(record['id'] ?? idx)}
                      className={`border-b hover:bg-muted/30 transition-colors ${grnDrillProps(record) ? ROW_DRILL_CLASS : ''}`}
                      {...(grnDrillProps(record) ?? rowDrillProps(getRowDrillUrl(record)))}
                    >
                      {fields.map((field) => (
                        <td
                          key={field.field}
                          className="py-3 px-4 text-sm whitespace-nowrap"
                        >
                          {renderCellValue(field.field, record[field.field], record)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex-shrink-0 -mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              itemLabel="lines"
            />
          </div>
        )}
      </DialogContent>

      {/* CR-7276: the second drill-down, stacked over this one so closing it
          returns to the same row in the same month rather than to the top. */}
      <LineGRNsModal
        poLineId={grnLineId}
        label={grnLineLabel}
        onClose={() => setGrnLineId(null)}
      />
    </Dialog>
  );
}
